/**
 * Video playback for clip layers on the LIVE editor canvas.
 *
 * Layers load poster-only (fabricVideoObject never attaches media), and this
 * manager attaches a <video> lazily, only for layers on the page that is
 * open. It then keeps every attached element in step with videoStore:
 *   - previewing and playing: the element PLAYS (smooth, with sound unless
 *     muted) and is re-seeked when it drifts past the tolerance;
 *   - otherwise: paused on the frame for the current scene-local time.
 * The pump repaints the canvas while anything plays.
 *
 * Unlike export (which seeks every frame), live playback may run on the wall
 * clock — this is a preview, and an 80 ms drift is invisible here.
 *
 * Attaching swaps `_element` only; nothing is set() and no Fabric event
 * fires, so editor history and autosave never see it, and toObject() keeps
 * writing the poster URL.
 */

import type { fabric } from 'fabric';
import { isVideoObject, videoObjectsOn, type CraftaraVideoObject } from './fabricVideoObject';
import { resolveMediaBlob } from './videoAssetUpload';
import { clipSourceMsAt } from './videoClip';
import { registerPumping, unregisterPumping } from './videoPump';

/** Re-seek a playing clip when it is further than this from where it should be. */
const DRIFT_TOLERANCE_MS = 80;
const DRIFT_CHECK_MS = 250;
/** Paused scrubbing ignores moves smaller than this (about one frame). */
const SCRUB_EPSILON_MS = 20;

/** Window event fired when a layer's media status changes (properties panel badge). */
export const VIDEO_MEDIA_STATUS_EVENT = 'craftara:video-media-status';

export interface LiveVideoSyncState {
  playing: boolean;
  /** Scene-local time to show. */
  localMs: number;
  /** Preview-only mute (timeline mute button). */
  muted: boolean;
}

export interface LiveVideoLayers {
  /** Attach media to any video layer that doesn't have it yet. */
  scan(): void;
  sync(state: LiveVideoSyncState): void;
  /** Re-apply the current state after a clip's trim / rate / loop changed. */
  refresh(obj?: fabric.Object | null): void;
  dispose(): void;
}

interface Entry {
  obj: CraftaraVideoObject;
  el: HTMLVideoElement;
  url: string;
  lastDriftCheck: number;
}

function announce(obj: CraftaraVideoObject): void {
  window.dispatchEvent(new CustomEvent(VIDEO_MEDIA_STATUS_EVENT, { detail: { obj } }));
}

function loadElement(blob: Blob): Promise<{ el: HTMLVideoElement; url: string } | null> {
  const url = URL.createObjectURL(blob);
  const el = document.createElement('video');
  el.preload = 'auto';
  el.muted = true;
  el.playsInline = true;
  return new Promise((resolve) => {
    const done = (ok: boolean) => {
      el.removeEventListener('loadeddata', onOk);
      el.removeEventListener('error', onErr);
      if (ok && el.videoWidth) {
        resolve({ el, url });
      } else {
        URL.revokeObjectURL(url);
        resolve(null);
      }
    };
    const onOk = () => done(true);
    const onErr = () => done(false);
    el.addEventListener('loadeddata', onOk);
    el.addEventListener('error', onErr);
    el.src = url;
  });
}

function release(entry: Entry): void {
  entry.el.pause();
  entry.el.removeAttribute('src');
  entry.el.load();
  URL.revokeObjectURL(entry.url);
}

export function createLiveVideoLayers(canvas: fabric.Canvas): LiveVideoLayers {
  const entries = new Map<CraftaraVideoObject, Entry>();
  const pending = new Set<CraftaraVideoObject>();
  let state: LiveVideoSyncState = { playing: false, localMs: 0, muted: false };
  let disposed = false;

  const onCanvas = (obj: CraftaraVideoObject): boolean =>
    videoObjectsOn(canvas).includes(obj);

  const apply = (entry: Entry, force = false): void => {
    const { obj, el } = entry;
    const clip = obj.craftaraVideo;
    const targetMs = clipSourceMsAt(clip, state.localMs);
    const currentMs = el.currentTime * 1000;

    if (state.playing) {
      // Clip sound is mixed into the export only. Several <video> elements
      // plus the music track drift apart and phase, so preview stays muted.
      el.muted = true;
      el.playbackRate = clip.rate;
      const now = performance.now();
      const ended = !clip.loop && targetMs >= clip.trimEndMs - 2;
      if (ended) {
        if (!el.paused) el.pause();
      } else if (el.paused) {
        el.currentTime = targetMs / 1000;
        void el.play().catch(() => undefined);
      } else if (force || now - entry.lastDriftCheck >= DRIFT_CHECK_MS) {
        entry.lastDriftCheck = now;
        // Covers loop wrap-around and trim edits as well as real drift.
        if (Math.abs(currentMs - targetMs) > DRIFT_TOLERANCE_MS) el.currentTime = targetMs / 1000;
      }
      registerPumping(canvas, obj);
      return;
    }

    if (!el.paused) el.pause();
    unregisterPumping(obj);
    if (force || Math.abs(currentMs - targetMs) > SCRUB_EPSILON_MS) {
      el.currentTime = targetMs / 1000;
      el.addEventListener('seeked', () => canvas.requestRenderAll(), { once: true });
    }
  };

  const attach = async (obj: CraftaraVideoObject): Promise<void> => {
    pending.add(obj);
    try {
      const blob = await resolveMediaBlob(obj.craftaraVideo);
      const loaded = blob ? await loadElement(blob) : null;
      if (disposed || !onCanvas(obj)) {
        if (loaded) URL.revokeObjectURL(loaded.url);
        return;
      }
      if (!loaded) {
        obj._mediaStatus = 'missing';
        announce(obj);
        return;
      }
      obj.attachElement(loaded.el);
      const entry: Entry = { obj, el: loaded.el, url: loaded.url, lastDriftCheck: 0 };
      entries.set(obj, entry);
      apply(entry, true);
      canvas.requestRenderAll();
      announce(obj);
    } finally {
      pending.delete(obj);
    }
  };

  const detach = (obj: CraftaraVideoObject): void => {
    const entry = entries.get(obj);
    if (!entry) return;
    entries.delete(obj);
    unregisterPumping(obj);
    obj.detachElement();
    release(entry);
  };

  const scan = (): void => {
    if (disposed) return;
    const live = new Set(videoObjectsOn(canvas));
    for (const obj of Array.from(entries.keys())) {
      if (!live.has(obj)) detach(obj);
    }
    for (const obj of live) {
      if (entries.has(obj) || pending.has(obj) || obj._mediaStatus === 'missing') continue;
      void attach(obj);
    }
  };

  // Coalesce: a page load adds every object in one burst.
  let scanQueued = false;
  const queueScan = (): void => {
    if (scanQueued) return;
    scanQueued = true;
    queueMicrotask(() => {
      scanQueued = false;
      scan();
    });
  };
  const onAdded = (e: fabric.IEvent): void => {
    if (!e.target || isVideoObject(e.target) || (e.target as fabric.Group)._objects) queueScan();
  };
  const onRemoved = (): void => queueScan();
  canvas.on('object:added', onAdded);
  canvas.on('object:removed', onRemoved);

  return {
    scan,
    sync(next) {
      state = next;
      entries.forEach((entry) => apply(entry));
    },
    refresh(obj) {
      if (obj && isVideoObject(obj)) {
        const entry = entries.get(obj);
        if (entry && obj._videoEl === entry.el) {
          apply(entry, true);
          return;
        }
        // Media was replaced (re-import) or never loaded: drop the old element and retry.
        if (entry) {
          entries.delete(obj);
          unregisterPumping(obj);
          release(entry);
        }
        if (obj._mediaStatus === 'missing') obj._mediaStatus = 'poster';
        queueScan();
        return;
      }
      entries.forEach((entry) => apply(entry, true));
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      canvas.off('object:added', onAdded);
      canvas.off('object:removed', onRemoved);
      for (const obj of Array.from(entries.keys())) detach(obj);
    },
  };
}
